/**
 * Case Status Constants
 * Centralized definitions for case statuses and their associated behaviors
 */

// Status IDs
export const CASE_STATUS = {
  ERROR: -1,
  DRAFT: 0,
  AI_REVIEW: 1,
  HUMAN_REVIEW: 2,
  FINISHED: 4,
  ARCHIVED: 6,
} as const;

// Status display properties
export const STATUS_COLORS = {
  [CASE_STATUS.ERROR]: "bg-destructive-subtle text-destructive-text",
  [CASE_STATUS.DRAFT]: "bg-muted text-secondary-foreground",
  [CASE_STATUS.AI_REVIEW]: "bg-ai-subtle text-ai-text",
  [CASE_STATUS.HUMAN_REVIEW]: "bg-info-subtle-strong text-info-text",
  [CASE_STATUS.FINISHED]: "bg-success-subtle-strong text-success-text",
  [CASE_STATUS.ARCHIVED]: "border border-border-strong text-muted-foreground",
} as const;

export const STATUS_NAMES = {
  [CASE_STATUS.ERROR]: "Error",
  [CASE_STATUS.DRAFT]: "Draft",
  [CASE_STATUS.AI_REVIEW]: "AI review",
  [CASE_STATUS.HUMAN_REVIEW]: "Human review",
  [CASE_STATUS.FINISHED]: "Finished",
  [CASE_STATUS.ARCHIVED]: "Archived",
} as const;

export const STATUS_DESCRIPTIONS = {
  [CASE_STATUS.ERROR]: "Failed during AI processing",
  [CASE_STATUS.DRAFT]: "Not yet submitted for review",
  [CASE_STATUS.AI_REVIEW]: "Being processed by Paribus AI",
  [CASE_STATUS.HUMAN_REVIEW]: "In review by a human analyst",
  [CASE_STATUS.FINISHED]: "Finished by a human analyst",
  [CASE_STATUS.ARCHIVED]: "Archived by the physician",
} as const;

export const STATUS_FEATURES = {
  // Statuses that navigate to files page on row click (instead of chronology)
  NAVIGATE_TO_FILES: [
    CASE_STATUS.DRAFT,
    CASE_STATUS.AI_REVIEW,
    CASE_STATUS.ARCHIVED,
    CASE_STATUS.ERROR,
  ],

  // Statuses that show the "View Chronology" icon
  SHOW_CHRON_ICON: [CASE_STATUS.HUMAN_REVIEW, CASE_STATUS.FINISHED],

  // Statuses that allow rerunning chronology
  ALLOW_RERUN_CHRONOLOGY: [CASE_STATUS.HUMAN_REVIEW, CASE_STATUS.FINISHED, CASE_STATUS.ERROR],

  // Statuses that allow downloading chronology
  ALLOW_DOWNLOAD: [CASE_STATUS.HUMAN_REVIEW, CASE_STATUS.FINISHED],

  // Statuses that show preview modal for professionals (instead of direct navigation)
  SHOW_PREVIEW_MODAL: [
    CASE_STATUS.DRAFT,
    CASE_STATUS.AI_REVIEW,
    CASE_STATUS.HUMAN_REVIEW,
    CASE_STATUS.ERROR,
    CASE_STATUS.ARCHIVED,
  ],
} as const;

export const statusHelpers = {
  shouldNavigateToFiles: (statusId: number): boolean => {
    return (STATUS_FEATURES.NAVIGATE_TO_FILES as readonly number[]).includes(statusId);
  },

  shouldShowChronIcon: (statusId: number): boolean => {
    return (STATUS_FEATURES.SHOW_CHRON_ICON as readonly number[]).includes(statusId);
  },

  canRerunChronology: (statusId: number): boolean => {
    return (STATUS_FEATURES.ALLOW_RERUN_CHRONOLOGY as readonly number[]).includes(statusId);
  },

  canDownload: (statusId: number): boolean => {
    return (STATUS_FEATURES.ALLOW_DOWNLOAD as readonly number[]).includes(statusId);
  },

  shouldShowPreviewModal: (statusId: number): boolean => {
    return (STATUS_FEATURES.SHOW_PREVIEW_MODAL as readonly number[]).includes(statusId);
  },
};
